"use client";

import type { HeadingTag } from "@/app/utilities/types";
import NextImage, { type ImageProps as NextImageProps } from "next/image";
import NextLink, { type LinkProps as NextLinkProps } from "next/link";
import { useTranslations } from "next-intl";

import { CommunityBuzzCarousel } from "@/composite/CommunityBuzzCarousel";
import type { CommunityBuzzCardProps } from "@/composite/CommunityBuzzCarousel/CommunityBuzzCard";
import {
	CommunityBuzzCarouselReference,
	ProductReference,
	RecipeReference
} from "@/types";
import { resolveImageProps } from "@/app/utilities/images";

interface CommunityProductItemReference {
	contentType: "product" | "recipe";
	referenceId: string;
	item: ProductReference | RecipeReference;
}

export interface CommunityBuzzCarouselBlockClientProps extends CommunityBuzzCarouselReference {
	cardsContent?: CommunityBuzzCarouselReference["card_content"] & {
		product?: CommunityProductItemReference[];
	};
}

/**
 * Featured Card block component
 */
export function CommunityBuzzCarouselBlock(
	props: CommunityBuzzCarouselBlockClientProps
): JSX.Element {
	const t = useTranslations("communityBuzzCarousel");

	const formattedSlides = props.cardsContent?.map<
		CommunityBuzzCardProps<NextImageProps, NextLinkProps>
	>((content) => ({
		author: content.author_name,
		actionLabel: t("actionLabel"),
		coverImage: {
			component: NextImage,
			...resolveImageProps("2:3", content?.carousel_2_3_teaser_image)
		},
		mediaType: content.asset?.video_url ? "video" : "image",
		details: {
			heading: content.modal_title?.headline,
			headingTag: content.modal_title?.headline_tag,
			theme: content.theme || "16:9",
			description: content.modal_description,
			image: content.asset?.image_url
				? {
						component: NextImage,
						...resolveImageProps("16:9", content.asset)
					}
				: undefined,
			video: content.asset?.video_url || undefined,
			productSectionTitle: content?.section_title,
			// TODO: Update product data with ecommerce service
			products: content?.product?.map(({ item }) => {
				return {
					name: item?.title,
					headingTag: "h5",
					price: {
						productValue: {
							value: 300
						}
					},
					rating: {
						rating: 5,
						label: "5.0 (54)"
					},
					image: item?.image?.image
						? {
								component: NextImage,
								...resolveImageProps("1:1", item?.image)
							}
						: undefined
				};
			}),
			cta: {
				component: NextLink,
				children: content.modal_button?.link?.title,
				href: content.modal_button?.link?.href,
				target: content.modal_button?.open_new_tab ? "_blank" : "_self"
			}
		}
	}));

	return (
		<CommunityBuzzCarousel<NextImageProps, NextLinkProps>
			headline={props.section_title?.headline}
			headingTag={props.section_title?.headline_tag as HeadingTag}
			cta={{
				component: NextLink,
				children: props.carousel_cta?.link?.title,
				href: props.carousel_cta?.link?.href,
				target: props.carousel_cta?.open_new_tab ? "_blank" : "_self"
			}}
			slides={formattedSlides || []}
		/>
	);
}
